import {
  LitElement,
  html,
  nothing,
} from '@lit'
import { formatMoney, t } from '@nova/utils'

class NovaCustomCalculatorValues extends LitElement {
  static properties = {
    shape: { type: String, attribute: 'shape' },
    unitType: { type: String, attribute: 'unit-type' },
    basePrice: { type: Number, attribute: 'base-price' },
    basePrice2: { type: Number, attribute: 'base-price-2' },
    basePrice3: { type: Number, attribute: 'base-price-3' },
    basePrice4: { type: Number, attribute: 'base-price-4' },
    widthRange: { type: Object, attribute: 'width-range' },
    lengthRange: { type: Object, attribute: 'length-range' },
    qty: { type: Number, attribute: 'qty' },
    isB2B: { type: Boolean, attribute: 'is-b2b', converter: (value) => value === "true" },
    b2bDiscount: { type: Number, attribute: 'b2b-discount' },
    b2bDiscountPromo: { type: Number, attribute: 'b2b-discount-promo' },
    b2bDiscountPromoStartDate: { type: String, attribute: 'b2b-discount-promo-start-date' },
    b2bDiscountPromoEndDate: { type: String, attribute: 'b2b-discount-promo-end-date' },
    d2cDiscountEnabled: { type: String, attribute: 'd2c-discount-enabled' },
    d2cDiscountStartDate: { type: String, attribute: 'd2c-discount-start-date' },
    d2cDiscountEndDate: { type: String, attribute: 'd2c-discount-end-date' },
    d2cDiscountPercentage: { type: Number, attribute: 'd2c-discount-percentage' },
    catalogType: { type: String, attribute: 'catalog-type' },
    discountType: { type: String, attribute: 'discount-type' },
    catalogConfig: { type: Object, attribute: 'catalog-config' },
    minWidthError: { type: String, attribute: 'min-width-error' },
    maxWidthError: { type: String, attribute: 'max-width-error' },
    minLengthError: { type: String, attribute: 'min-length-error' },
    maxLengthError: { type: String, attribute: 'max-length-error' },
  }

  connectedCallback() {
    super.connectedCallback()

    this.footToInches = 12
    this.meterToCentimeter = 100
    this.inchToCentimeter = 2.54

    this.$propWidth = document.querySelector('[name="properties[_custom_width]"]')
    this.$propLength = document.querySelector('[name="properties[_custom_length]"]')
    this.$propShape = document.querySelector('[name="properties[_custom_shape]"]')
    this.$propIsCustom = document.querySelector('[name="properties[_is_custom_size]"]')
    this.$propType = document.querySelector('[name="properties[_custom_type]"]')
    this.$propWidthShow = document.querySelector('[name="properties[Width]"]')
    this.$propLengthShow = document.querySelector('[name="properties[Length]"]')
    this.$propIsB2B = document.querySelector('[name="properties[_is_b2b]"]')
    this.$propB2BDiscount = document.querySelector('[name="properties[_b2b_discount]"]')
    this.$propB2BDiscountPromo = document.querySelector('[name="properties[_b2b_discount_promo]"]')
    this.$propB2BDiscountPromoStartDate = document.querySelector('[name="properties[_b2b_discount_promo_start_date]"]')
    this.$propB2BDiscountPromoEndDate = document.querySelector('[name="properties[_b2b_discount_promo_end_date]"]')
    this.$propD2CDiscountPercentage = document.querySelector('[name="properties[_d2c_discount_percentage]"]')
    this.$propCatalogType = document.querySelector('[name="properties[_catalog_type]"]')
    this.$propDiscountType = document.querySelector('[name="properties[_discount_type]"]')

    this.$sizeElem = document.querySelector('[name="size"]:checked')
    this.$priceWrapper = document.querySelector('.product__info-wrapper .js-price')
    this.$priceElems = [...document.querySelectorAll('.product__info-wrapper .js-product-custom-price')]
    this.$priceCompareElem = document.querySelector('.product__info-wrapper .price-item--compare')
    this.$priceCompareStandard = document.querySelector('.price-item--compare__standard')
    this.$priceCompareCustom = document.querySelector('.price-item--compare__custom')
    this.$srpCustomElem = document.querySelector('.js-product-custom-srp')

    this.swapSize = this.swapSize.bind(this)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  firstUpdated() {
    this.initialize()
  }

  updated(changedProperties) {
    if (changedProperties.has('unitType') && changedProperties.get('unitType')) {
      this.requestUpdate()

      this.initialize()
    }

    if (changedProperties.has('qty') && this.$sizeElem) {
      if (this.$sizeElem.value === 'custom-placemat' || this.$sizeElem.value === 'custom-runner') {
        this.requestUpdate()
        this.onCalculatePrice()
      }
    }
  }

  initialize() {
    this.inputFields = [...this.shadowRoot.querySelectorAll('sl-input')]
    this.inputFields.forEach((item) => {
      item.value = ''
      item.addEventListener('sl-blur', () => this.onInputChange(item))
    })

    if (this.$propWidth && this.$propWidth.value) {
      this.$propWidth.value = ''
      this.$propLength.value = ''
      this.$propShape.value = ''
      this.$propIsCustom.value = ''
      this.$propWidthShow.value = ''
      this.$propLengthShow.value = ''
      this.$propType.value = ''
      this.$propIsB2B.value = ''
      this.$propB2BDiscount.value = ''
      this.$propB2BDiscountPromo.value = ''
      this.$propB2BDiscountPromoStartDate.value = ''
      this.$propB2BDiscountPromoEndDate.value = ''
      this.$propD2CDiscountPercentage.value = ''
      this.$propCatalogType.value = ''
      this.$propDiscountType.value = ''

      this.onCalculatePrice()
    }
  }

  get typeBySizeOption() {
    let type = ''
    switch (this.$sizeElem.value) {
      case 'custom-placemat':
      case 'custom-runner':
        type = 'tabletop'
        break
      case 'custom-worktop':
        type = 'worktop'
        break
      case 'custom-shag-mat':
        type = 'shag'
        break
      default:
        type = 'rug'
        break
    }

    return type
  }

  safeCeil(num, decimals = 2) {
    const factor = Math.pow(10, decimals);
    return Math.ceil(num * factor + Number.EPSILON) / factor;
  }

  applyCalculatedValue(inputFields, type) {
    let calculatedValue = inputFields[0].value * this.footToInches + Number(inputFields[1].value)

    if (this.unitType === 'metric') {
      calculatedValue = (inputFields[0].value * this.meterToCentimeter + Number(inputFields[1].value)) / this.inchToCentimeter
      calculatedValue = this.safeCeil(calculatedValue)
    }

    if (type === 'w') {
      this.$propWidth.value = calculatedValue
      this.$propWidthShow.value = `${calculatedValue}${t('products.custom_calculator.units.in')}`
      if (this.shape === 'round' || this.shape === 'square') {
        this.$propLength.value = calculatedValue
        this.$propLengthShow.value = `${calculatedValue}${t('products.custom_calculator.units.in')}`
      }
    } else if (type === 'h') {
      this.$propLength.value = calculatedValue
      this.$propLengthShow.value = `${calculatedValue}${t('products.custom_calculator.units.in')}`
    }
  }

  onInputChange(item) {
    item.value = item.value.replaceAll('-', '')

    const type = item.dataset.type
    const $inputs = this.inputFields.filter(input => input.dataset.type === type)
    this.applyCalculatedValue($inputs, type)

    this.$propShape.value = this.shape
    this.$propIsCustom.value = 'true'
    this.$propType.value = this.typeBySizeOption
    this.$propIsB2B.value = this.isB2B ? 'true' : 'false'
    this.$propB2BDiscount.value = this.b2bDiscount || 0
    this.$propB2BDiscountPromo.value = this.b2bDiscountPromo || 0
    this.$propB2BDiscountPromoStartDate.value = this.b2bDiscountPromoStartDate
    this.$propB2BDiscountPromoEndDate.value = this.b2bDiscountPromoEndDate
    this.$propD2CDiscountPercentage.value = this.d2cDiscountPercentage
    this.$propCatalogType.value = this.catalogType || 'consumer'
    this.$propDiscountType.value = this.discountType || 'standard'


    this.onCalculatePrice()
    this.requestUpdate()
  }

  calculatedWidth(width) {
    let calWidth = width
    if (width) {
      switch (this.$sizeElem.value) {
        case 'custom-rug':
        case 'custom-placemat':
        case 'custom-runner':
        case 'custom-worktop':
          if (width <= 72) {
            calWidth = 72
          } else if (width <= 144) {
            calWidth = 144
          } else if (width <= 216) {
            calWidth = 216
          }
          break
        default:
          calWidth = width // fallback value
          break
      }
    }
    return calWidth
  }

  get basePriceByShape() {
    switch (this.shape) {
      case 'rectangle':
      case 'square':
        return this.basePrice
      case 'runner':
        return this.basePrice2
      case 'oval':
      case 'round':
        return this.basePrice3
      case 'special-shape':
        return this.basePrice4
    }
  }

  calculateMSRP(width, length) {
    const calWidth = this.calculatedWidth(width)
    const sqft = (calWidth * length) / (this.footToInches * this.footToInches)
    const pricePerSqft = this.basePriceByShape
    const price = +((pricePerSqft * sqft) * 100).toFixed(2)
    let total = price
    // Apply B2B discount based on catalog configuration
    if (this.isB2B && this.catalogConfig) {
      const discountConfig = this.catalogConfig;
      if (discountConfig.discount_type?.value === 'compound') {
        // Compound discount (e.g., F+B Domestic: 20% + 50%)
        const firstDiscount = 1 - (discountConfig.calculator_discount / 100);
        const secondDiscount = 1 - (discountConfig.secondary_discount / 100);
        total = +(total * firstDiscount * secondDiscount).toFixed(2);
      } else if (discountConfig.discount_type?.value === 'tiered') {
        // Tiered discount based on order value (future enhancement)
        const discount = discountConfig.calculator_discount || 0;
        const discountMultiplier = 1 - (discount / 100);
        total = +(total * discountMultiplier).toFixed(2);
      }
      else {
        // Standard single discount
        const discount = discountConfig.calculator_discount || 0;
        if (discount > 0) {
          const discountMultiplier = 1 - (discount / 100);
          total = +(total * discountMultiplier).toFixed(2);
        }
      }
    }

    const beforePromo = total 

    if (this.isB2B && Number(this.b2bDiscountPromo) > 0 && this.isTodayWithinInclusive(this.b2bDiscountPromoStartDate, this.b2bDiscountPromoEndDate)) {
      const promoMultiplier = 1 - (Number(this.b2bDiscountPromo) / 100)
      total = +(total * promoMultiplier).toFixed(2)
    }

    if (!this.isB2B && Number(this.d2cDiscountPercentage) > 0) {
      const promoMultiplier = 1 - (Number(this.d2cDiscountPercentage) / 100)
      total = +(total * promoMultiplier).toFixed(2)
    }

    return {
      sqft: +sqft,
      pricePerSqft: +pricePerSqft.toFixed(2),
      price,
      beforePromo,
      msrp: total,
      b2bDiscount: this.isB2B ? this.b2bDiscount : 0,
      catalogType: this.catalogType || 'consumer',
      discountType: this.discountType || 'standard',
    }
  }

  onCalculatePrice() {
    this.submitBtn = document.querySelector('.js-product-buy-button')

    let w = Number(this.$propWidth.value)
    let h = Number(this.$propLength.value)

    if (w && h) {
      if (this.widthRange && (this.widthRange.min > w || this.widthRange.max < w)) {
        this.$priceElems.forEach(item => item.textContent = '-')
        this.submitBtn?.setAttribute('disabled', true)
        w = 0
      }
      if (this.lengthRange && (this.lengthRange.min > h || this.lengthRange.max < h)) {
        this.$priceElems.forEach(item => item.textContent = '-')
        this.submitBtn?.setAttribute('disabled', true)
        h = 0
      }

      if (w && h) {
        if (w > h) {
          this.swapSize(w, h)
        } else {
          const priceInfo = this.calculateMSRP(w, h)
          
          this.showSRPForB2B(priceInfo.price)
          this.submitBtn?.removeAttribute('disabled')
          this.$priceElems.forEach(item => item.textContent = formatMoney(priceInfo.msrp))

          if (priceInfo.beforePromo > priceInfo.msrp) {
            this.$priceElems.forEach((priceEl) => {
               let compareEl =
                priceEl.nextElementSibling && priceEl.nextElementSibling.matches('.price-item--compare__standard')
                  ? priceEl.nextElementSibling
                  : null;
              if (!compareEl) {
                const priceCompareEl = document.createElement('s');
                priceCompareEl.className = 'price-item--compare__standard p c-grey-400';
                priceEl.insertAdjacentElement('afterend', priceCompareEl);
                priceEl.parentElement.classList.add('flex', 'flex-items-center', 'gap-xs', 'flex-justify-start');
                priceCompareEl.textContent = formatMoney(priceInfo.beforePromo);
              } else {
                compareEl.textContent = formatMoney(priceInfo.beforePromo);
              }
            })
          }
        }
      } else {
        this.$priceElems.forEach(item => item.textContent = '-')
        this.$priceCompareElem.classList.add('hidden')
        this.submitBtn?.setAttribute('disabled', true)
      }
    } else {
      this.$priceElems.forEach(item => item.textContent = '-')
      this.$priceCompareElem.classList.add('hidden')
      this.submitBtn?.setAttribute('disabled', true)
    }

    if (this.shape === 'special-shape') {
      this.submitBtn?.setAttribute('disabled', true)
    }

    document.dispatchEvent(
      new CustomEvent('nova:product:render-custom-calculator-preview', {
        detail: {
          w,
          h,
        },
      }),
    )
  }

  showSRPForB2B(price) {
    if (this.isB2B && this.b2bDiscount) {
      this.$priceWrapper.classList.add(...['price--on-sale', 'price--on-msrp'])
      this.$priceCompareElem.classList.remove('hidden')
      this.$priceCompareStandard.classList.add('hidden')
      this.$priceCompareCustom.classList.remove('hidden')
      this.$srpCustomElem.textContent = formatMoney(price)
    } else {
      this.$priceCompareElem.classList.add('hidden')
    }
  }

  swapSize(w, h) {
    const $inputsW = this.inputFields.filter(input => input.dataset.type === 'w')
    const oldW = $inputsW.map(item => item.value)
    const $inputsH = this.inputFields.filter(input => input.dataset.type === 'h')
    $inputsH.forEach((item, index) => {
      $inputsW[index].value = item.value
      item.value = oldW[index]
    })

    this.applyCalculatedValue($inputsW, 'w')
    this.applyCalculatedValue($inputsH, 'h')

    const priceInfo = this.calculateMSRP(h, w)
    this.$priceElems.forEach(item => item.textContent = formatMoney(priceInfo.msrp))
    this.showSRPForB2B(priceInfo.price)
    this.submitBtn?.removeAttribute('disabled')
  }

  parseYmd(dateStr) {
    if (!dateStr || typeof dateStr !== 'string') return null;
    const [y, m, d] = dateStr.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }

  isTodayWithinInclusive(startStr, endStr) {
    const today = new Date();
    const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const start = this.parseYmd(startStr);
    const end   = this.parseYmd(endStr);

    if (start && end)    { return todayMid >= start && todayMid <= end; }
    if (start && !end)   { return todayMid >= start; }
    if (!start && end)   { return todayMid <= end; }
    return true;
  }

  renderInputs(type) {
    let firstLabel = t('products.custom_calculator.units.feet')
    let secondLabel = t('products.custom_calculator.units.inches')
    if (this.unitType === 'metric') {
      firstLabel = t('products.custom_calculator.units.meter')
      secondLabel = t('products.custom_calculator.units.centimeter')
    }

    return html`
      <div part="unit-inputs">
        <sl-input
          size="medium"
          type="number"
          min="1"
          no-spin-buttons
          required
          part="unit-input"
          data-type="${type}"
          autocomplete="off"
        >
          <span slot="suffix" part="unit-label">${firstLabel}</span>
        </sl-input>
        <sl-input
          size="medium"
          type="number"
          min="1"
          no-spin-buttons
          part="unit-input"
          data-type="${type}"
          autocomplete="off"
        >
          <span slot="suffix" part="unit-label">${secondLabel}</span>
        </sl-input>
      </div>
    `
  }

  showMinMax(range) {
    return range
      ? html`<span part="range">${t('products.custom_calculator.labels.min')} ${range.min}${t('products.custom_calculator.units.in')}, ${t('products.custom_calculator.labels.max')} ${range.max}</span>`
      : nothing
  }

  showError(range, isWidth = true) {
    if (range) {
      let value = Number(this.$propWidth.value)
      let minError = this.minWidthError
      let maxError = this.maxWidthError
      if (!isWidth) {
        value = Number(this.$propLength.value)
        minError = this.minLengthError
        maxError = this.maxLengthError
      }

      if (value) {
        if (Number(range.min) && Number(range.min) > value) {
          return html`<div part="error">${minError}</div>`
        }
        if (Number(range.max) && Number(range.max) < value) {
          return html`<div part="error">${maxError}</div>`
        }
      }
    }
    return nothing
  }

  renderFields() {
    switch (this.shape) {
      case 'rectangle':
      case 'runner':
      case 'special-shape':
      case 'oval':
        return html`
          <div class="" slot="base" part="container">
            <div part="block">
              <div part="label">
                ${t('products.custom_calculator.labels.width')}
                ${this.showMinMax(this.widthRange)}
              </div>
              ${this.showError(this.widthRange)}
              ${this.renderInputs('w')}
            </div>
            <div part="block">
              <div part="label">
                ${t('products.custom_calculator.labels.length')}
                ${this.showMinMax(this.lengthRange)}
              </div>
              ${this.showError(this.lengthRange, false)}
              ${this.renderInputs('h')}
            </div>
          </div>
        `
      case 'round':
      case 'square':
        return html`
          <div class="" slot="base" part="container">
            <div part="block">
              <div part="label">
                ${this.showMinMax(this.widthRange)}
              </div>
              ${this.showError(this.widthRange)}
              ${this.renderInputs('w')}
            </div>
          </div>
        `
      default:
        break
    }

    return nothing
  }

  render() {
    return html`
      <div part="base">
        <slot name="base"></slot>
        ${this.renderFields()}
      </div>
    `
  }
}

if (typeof customElements.get('nova-custom-calculator-values') === 'undefined') {
  customElements.define('nova-custom-calculator-values', NovaCustomCalculatorValues)
}
